import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiBriefcase, FiArrowRight, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import Logo from '../components/Logo';
import { playClick, playError } from '../utils/sounds';

const API = process.env.REACT_APP_API_URL || '';

export default function CompanyCode() {
  const { setCompanyCode } = useAuth();
  const site = useSite();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const clean = code.trim();
    if (!clean) { setError('Enter a company code'); return; }
    setBusy(true);
    try {
      // First, ask the hub-backed server-status endpoint whether this
      // tenant's server is currently disabled. If so, surface the portal
      // link inline rather than letting the user proceed to /login.
      try {
        const statusRes = await fetch(`${API}/api/server-status?code=${encodeURIComponent(clean)}`, { credentials: 'include' });
        const status = statusRes.ok ? await statusRes.json() : null;
        if (status && status.enabled === false) {
          const portal = status.portalUrl || 'https://mikesapphub.com';
          setError(`${status.message || 'This server has been disabled.'} See ${portal}`);
          setBusy(false);
          playError();
          return;
        }
      } catch { /* hub unreachable — fall through, the resolve below still acts as a gate */ }

      const res = await fetch(`${API}/api/tenant/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company_code: clean })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.error || 'Unknown company code');
        setBusy(false);
        playError();
        return;
      }
      playClick();
      setCompanyCode(data.company_code, data.server_name || data.company || '');
      navigate('/login');
    } catch (ex) {
      setError('Network error — is the server reachable?');
      setBusy(false);
      playError();
    }
  };

  return (
    <div className="gradient-bg" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflow: 'hidden'
    }}>
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          animate={{ y: [-20, 20, -20], x: [-10, 10, -10], opacity: [0.05, 0.15, 0.05] }}
          transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.5 }}
          style={{
            position: 'absolute', display: window.innerWidth < 500 ? 'none' : 'block',
            width: 100 + i * 30, height: 100 + i * 30,
            borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)',
            top: `${10 + i * 12}%`, left: `${5 + i * 15}%`
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, type: 'spring' }}
        style={{
          width: '100%', maxWidth: 420, background: 'var(--bg-card)',
          borderRadius: 20, padding: 'clamp(24px, 5vw, 48px) clamp(20px, 4vw, 36px)', border: '1px solid var(--border)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', position: 'relative', zIndex: 1
        }}
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          style={{ textAlign: 'center', marginBottom: 28 }}
        >
          <div style={{ margin: '0 auto 16px', width: 64, height: 64 }}>
            {site?.logoUrl ? (
              <img src={site.logoUrl} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'contain' }} />
            ) : (
              <Logo size={64} />
            )}
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)' }}>
            {site?.site_name || 'Asset Logic'}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Enter your company code to continue</p>
        </motion.div>

        <form onSubmit={submit}>
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
            style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Company code
            </label>
            <div style={{ position: 'relative' }}>
              <FiBriefcase size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input type="text"
                autoFocus={typeof window !== 'undefined' && window.innerWidth >= 768 && !/iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent || '')}
                autoComplete="off" spellCheck={false}
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                placeholder="COMPANY-CODE"
                style={{
                  width: '100%', padding: '12px 14px 12px 40px', borderRadius: 10,
                  border: '1px solid var(--border)', background: 'var(--bg-input)',
                  color: 'var(--text-primary)', fontSize: 15, letterSpacing: 1,
                  fontWeight: 600, textTransform: 'uppercase',
                  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
                }} />
            </div>
          </motion.div>

          {error && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 10,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                color: '#ef4444', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8
              }}>
              <FiAlertCircle size={14} /> {error}
            </motion.div>
          )}

          <motion.button type="submit" disabled={busy}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            style={{
              width: '100%', padding: '13px 16px', borderRadius: 10, border: 'none',
              background: busy ? '#94a3b8' : 'linear-gradient(135deg, #2563eb, #4f46e5)',
              color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: busy ? 'none' : '0 8px 20px rgba(37,99,235,0.35)'
            }}>
            {busy ? 'Checking…' : (<>Continue <FiArrowRight size={16} /></>)}
          </motion.button>
        </form>

        <p style={{ marginTop: 20, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
          Don't have a company code? Contact your administrator.
        </p>
        <p style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
          Want your own server?{' '}
          <a href="https://mikesapphub.com" target="_blank" rel="noopener noreferrer"
            style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
            mikesapphub.com
          </a>
        </p>
        <p style={{ marginTop: 14, textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', opacity: 0.7 }}>
          Powered by Asset Logic
        </p>
      </motion.div>
    </div>
  );
}
